import React from 'react';
import { Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import { Button } from '@loader/ui';
import { Input } from '@loader/ui';
import type { FilterOperator } from '@loader/shared-types';
import { FILTER_OPERATORS_BY_TYPE } from '@loader/shared-types';

import { getOperatorLabel } from '@/features/linkedTables/editLinkedTable/components/FilterEditor/types';
import type { LinkedTableFilter } from '@/lib/types';

/** Static operator options for text fields (no field-type context available in this component) */
const OPERATOR_OPTIONS = FILTER_OPERATORS_BY_TYPE.text.map((op: FilterOperator) => ({
  value: op,
  label: getOperatorLabel(op),
}));

interface LinkedTableFilterBuilderProps {
  filters: LinkedTableFilter[];
  availableFields: string[];
  onFiltersChange: (filters: LinkedTableFilter[]) => void;
  onAddFilter: () => void;
  onRemoveFilter: (filterId: string) => void;
}

export const LinkedTableFilterBuilder: React.FC<LinkedTableFilterBuilderProps> = ({
  filters,
  availableFields,
  onFiltersChange,
  onAddFilter,
  onRemoveFilter,
}) => {
  const handleFilterChange = (
    filterId: string,
    field: keyof LinkedTableFilter,
    value: unknown
  ) => {
    const newFilters = filters.map((filter) =>
      filter.id === filterId ? { ...filter, [field]: value } : filter
    );
    onFiltersChange(newFilters);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Filters</h3>
        <Button onClick={onAddFilter} size="sm">
          Add Filter
        </Button>
      </div>

      {filters.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          No filters added yet. Click "Add Filter" to create your first filter.
        </div>
      ) : (
        <div className="space-y-3">
          {filters.map((filter, index) => (
            <div
              key={filter.id}
              className="flex items-center gap-2 rounded-lg border p-3"
            >
              <div className="w-8 text-sm font-medium text-gray-500">
                {index + 1}.
              </div>

              <div className="flex-1">
                <Select
                  value={filter.field_id}
                  onValueChange={(value) =>
                    handleFilterChange(filter.id, 'field_id', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields.map((field) => (
                      <SelectItem key={field} value={field}>
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-32">
                <Select
                  value={filter.operator}
                  onValueChange={(value) =>
                    handleFilterChange(filter.id, 'operator', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATOR_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1">
                <Input
                  placeholder="Value"
                  value={
                    Array.isArray(filter.value)
                      ? filter.value.join(', ')
                      : String(filter.value)
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    // Handle array values for 'in' and 'not_in' operators
                    if (
                      filter.operator === 'in' ||
                      filter.operator === 'not_in'
                    ) {
                      handleFilterChange(
                        filter.id,
                        'value',
                        value.split(',').map((v) => v.trim())
                      );
                    } else {
                      handleFilterChange(filter.id, 'value', value);
                    }
                  }}
                />
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveFilter(filter.id)}
                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkedTableFilterBuilder;
